// Closed outlines from an ASCII DXF, with no dependency.
//
// A cadastral plan (plan de amplasament și delimitare) arrives as a DXF in
// Stereo 70, the parcel drawn as an LWPOLYLINE, sometimes as an old-style
// POLYLINE, and now and then as loose LINEs that only meet at their ends.
// All three are read; arcs (bulges) are flattened. The caller picks which
// outline is the plot, because the same file also holds the neighbours,
// the buildings and the text frames.
import type { Pt, Ring } from "../geom.ts";

export interface DxfOutline {
  layer: string;
  source: "LWPOLYLINE" | "POLYLINE" | "LINE";
  ring: Ring;
  area: number;
}

export interface DxfResult {
  outlines: DxfOutline[];
  /** Open polylines and lines that never closed into a loop. */
  open: number;
}

type Pair = [number, string];

function pairs(text: string): Pair[] {
  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  const out: Pair[] = [];
  for (let i = 0; i + 1 < lines.length; i += 2) {
    const code = parseInt(lines[i].trim(), 10);
    if (Number.isNaN(code)) throw new Error(`Not an ASCII DXF: group code expected at line ${i + 1}`);
    out.push([code, lines[i + 1].trim()]);
  }
  return out;
}

const ringArea = (r: Ring) => {
  let s = 0;
  for (let i = 0; i < r.length; i++) { const [x1, y1] = r[i], [x2, y2] = r[(i + 1) % r.length]; s += x1 * y2 - x2 * y1; }
  return Math.abs(s) / 2;
};

/** Points along the arc from a to b given a DXF bulge (tan of a quarter of the angle). */
function arc(a: Pt, b: Pt, bulge: number, maxStep = Math.PI / 18): Pt[] {
  const theta = 4 * Math.atan(bulge);
  const chord = Math.hypot(b[0] - a[0], b[1] - a[1]);
  if (chord === 0 || Math.abs(theta) < 1e-9) return [];
  const r = chord / (2 * Math.sin(Math.abs(theta) / 2));
  const mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2;
  const d = Math.sqrt(Math.max(0, r * r - (chord / 2) ** 2));
  // centre lies to the left of a->b for a positive bulge with |theta| < pi
  const ux = (b[0] - a[0]) / chord, uy = (b[1] - a[1]) / chord;
  const side = (bulge > 0 ? 1 : -1) * (Math.abs(theta) > Math.PI ? -1 : 1);
  const cx = mx - uy * d * side, cy = my + ux * d * side;
  const a0 = Math.atan2(a[1] - cy, a[0] - cx);
  const steps = Math.max(2, Math.ceil(Math.abs(theta) / maxStep));
  const pts: Pt[] = [];
  for (let i = 1; i < steps; i++) {
    const t = a0 + (theta * i) / steps;
    pts.push([cx + r * Math.cos(t), cy + r * Math.sin(t)]);
  }
  return pts;
}

function withBulges(verts: { p: Pt; bulge: number }[], closed: boolean): Ring {
  const ring: Ring = [];
  for (let i = 0; i < verts.length; i++) {
    const v = verts[i];
    ring.push(v.p);
    const next = verts[i + 1] ?? (closed ? verts[0] : undefined);
    if (next && v.bulge) ring.push(...arc(v.p, next.p, v.bulge));
  }
  return ring;
}

const same = (a: Pt, b: Pt, tol: number) => Math.abs(a[0] - b[0]) <= tol && Math.abs(a[1] - b[1]) <= tol;

/** Chain loose segments that meet end to end into closed loops. */
function chain(segments: { a: Pt; b: Pt; layer: string }[], tol: number): { ring: Ring; layer: string }[] {
  const left = segments.slice();
  const loops: { ring: Ring; layer: string }[] = [];
  while (left.length) {
    const first = left.pop()!;
    const ring: Ring = [first.a, first.b];
    let closed = false;
    for (;;) {
      const end = ring[ring.length - 1];
      if (ring.length > 2 && same(end, ring[0], tol)) { ring.pop(); closed = true; break; }
      const i = left.findIndex((s) => same(s.a, end, tol) || same(s.b, end, tol));
      if (i < 0) break;
      const s = left.splice(i, 1)[0];
      ring.push(same(s.a, end, tol) ? s.b : s.a);
    }
    if (closed && ring.length >= 3) loops.push({ ring, layer: first.layer });
  }
  return loops;
}

export function parseDxf(text: string, tol = 0.01): DxfResult {
  const p = pairs(text);
  const outlines: DxfOutline[] = [];
  const segments: { a: Pt; b: Pt; layer: string }[] = [];
  let open = 0;

  // only the ENTITIES section; blocks would draw every title-block frame
  let i = p.findIndex(([c, v], k) => c === 2 && v === "ENTITIES" && p[k - 1]?.[1] === "SECTION");
  if (i < 0) i = 0;

  const push = (layer: string, source: DxfOutline["source"], ring: Ring) => {
    if (ring.length > 3 && same(ring[0], ring[ring.length - 1], tol)) ring = ring.slice(0, -1);
    if (ring.length >= 3) outlines.push({ layer, source, ring, area: ringArea(ring) });
  };

  while (i < p.length) {
    const [code, value] = p[i];
    if (code === 0 && value === "ENDSEC") break;
    if (code !== 0) { i++; continue; }

    if (value === "LWPOLYLINE") {
      let layer = "0", flags = 0;
      const verts: { p: Pt; bulge: number }[] = [];
      for (i++; i < p.length && p[i][0] !== 0; i++) {
        const [c, v] = p[i];
        if (c === 8) layer = v;
        else if (c === 70) flags = parseInt(v, 10);
        else if (c === 10) verts.push({ p: [parseFloat(v), 0], bulge: 0 });
        else if (c === 20 && verts.length) verts[verts.length - 1].p[1] = parseFloat(v);
        else if (c === 42 && verts.length) verts[verts.length - 1].bulge = parseFloat(v);
      }
      const closed = (flags & 1) === 1 || (verts.length > 2 && same(verts[0].p, verts[verts.length - 1].p, tol));
      if (closed) push(layer, "LWPOLYLINE", withBulges(verts, true));
      else { open++; for (let k = 0; k + 1 < verts.length; k++) segments.push({ a: verts[k].p, b: verts[k + 1].p, layer }); }
      continue;
    }

    if (value === "POLYLINE") {
      let layer = "0", flags = 0;
      for (i++; i < p.length && p[i][0] !== 0; i++) {
        if (p[i][0] === 8) layer = p[i][1];
        else if (p[i][0] === 70) flags = parseInt(p[i][1], 10);
      }
      const verts: { p: Pt; bulge: number }[] = [];
      while (i < p.length && p[i][0] === 0 && p[i][1] === "VERTEX") {
        const v = { p: [0, 0] as Pt, bulge: 0 };
        for (i++; i < p.length && p[i][0] !== 0; i++) {
          const [c, val] = p[i];
          if (c === 10) v.p[0] = parseFloat(val);
          else if (c === 20) v.p[1] = parseFloat(val);
          else if (c === 42) v.bulge = parseFloat(val);
        }
        verts.push(v);
      }
      if (p[i]?.[1] === "SEQEND") i++;
      const closed = (flags & 1) === 1 || (verts.length > 2 && same(verts[0].p, verts[verts.length - 1].p, tol));
      if (closed) push(layer, "POLYLINE", withBulges(verts, true));
      else open++;
      continue;
    }

    if (value === "LINE") {
      let layer = "0";
      const a: Pt = [0, 0], b: Pt = [0, 0];
      for (i++; i < p.length && p[i][0] !== 0; i++) {
        const [c, v] = p[i];
        if (c === 8) layer = v;
        else if (c === 10) a[0] = parseFloat(v);
        else if (c === 20) a[1] = parseFloat(v);
        else if (c === 11) b[0] = parseFloat(v);
        else if (c === 21) b[1] = parseFloat(v);
      }
      segments.push({ a, b, layer });
      continue;
    }
    i++;
  }

  for (const loop of chain(segments, tol)) push(loop.layer, "LINE", loop.ring);
  outlines.sort((x, y) => y.area - x.area);
  return { outlines, open };
}
